// Bouwstenen van het Planner To-Do-rooster: datumkop, taakbalk, dagcel,
// persoonsrij en de rijen van de "te plannen"-bak. Alles staat op één CSS-grid
// (naamkolom + één kolom per kalenderdag), zodat bak, kop en personen exact onder
// elkaar liggen en het raster de volle breedte vult.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { DAGSTAAT_STIJL } from '../../utils/capacityColors';
import {
  effectiveTaskHours, DEFAULT_CAPACITY_RULES, taskProjectLabel, taskProjectTaakRegel, taskProjectVoluit, taskProjectNaam, taskProjectNaamKort, taskPlTekst,
} from '../../utils/capacityCalc';
import {
  projectKleur, projectStijl, fmtUren, fmtHalfUren, initialen, rolTekst, werkdagenVoor, urenVan, tint,
  dagOverig, klapBlokkenUit, weekendRegels, dagDatum, kortTaak, blokSegmenten, sjabloonDelen, DRAAIDAG_ORANJE, dagSaldo,
} from './rooster';
import { daysUntil, formatISO } from './plannerDatums';
import { ResizeHandvat } from '../Dagstart/TaskBlock';
import KlantLogo from '../Dagstart/KlantLogo';
import { mijlpaalNaam, mijlpaalSegmenten, reviewSegmenten, werkSoortStijl, V1_ACCENT, MIJLPAAL_GEDEMPT } from '../../utils/roosterBetekenis';

// De breedte van de balk kort de projectnaam af (CSS-ellipsis), niet een vaste tekenlimiet:
// een brede balk toont de hele naam (Joris 8 okt: 'Videoboodschappen CEO Rut…' bij ruimte genoeg).
const NAAM_MAX = 120;

export const NAAM_BREEDTE = 236;

// Zelfde woord als Capaciteit: een afwezige dag heet "Afwezig"; "vrij" is open tijd.
const staatTekst = (staat) => (staat === 'vrij' ? 'Afwezig' : DAGSTAAT_STIJL[staat]?.tekst || '');
const staatTitel = (staat) => (staat === 'vrij'
  ? 'Afwezig — niet beschikbaar, hier kan niets gepland worden'
  : DAGSTAAT_STIJL[staat]?.titel || '');
export const LANE_H = 34;
const LANE_GAP = 3;
const VOET_H = 12;
const LEEG_H = 18; // rij zonder projectblokken

const vandaagISO = () => formatISO(new Date());

// Rust: één neutrale tint voor alles wat "geen werkdag" is (weekend, vrij, draaidag),
// geen arceringen. Kleur is voorbehouden aan projecten en aan rood = overboekt.
const NEUTRAAL = '#f3f4f6';
const ARCERING = NEUTRAAL;
const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const RAND = '#eceef1';
const ROOD = '#dc2626';
// Afspraken en klein werk: dunne grijze vulling onderin de dag, hoogte = aandeel van de dag.
const VULLING = '#e6e8ec';

/** Randen/tinten per kolom: weekscheiding op maandag, vandaag gemarkeerd. */
function kolomRand(k) {
  if (k.maandag) return '1px solid #d9dde3';
  return `1px solid ${RAND}`;
}

// ─── Luie tooltip ────────────────────────────────────────────────────────────

/**
 * Tooltip die pas bestaat zodra je over het blok beweegt (of het focust). Gemeten (6 okt,
 * 36 personen × 1 week): een MUI-Tooltip om elk blok was het grootste deel van de rendertijd
 * van een vensterwissel. De meeste blokken worden nooit aangewezen; die betalen nu niets.
 * Het blok zelf blijft altijd hetzelfde element (geen remount: focus en sleep blijven heel);
 * de tooltip hangt er als los, verborgen anker naast en richt zich op het blok (PopperProps).
 * `title` mag een functie zijn: de inhoud wordt dan pas bij openen opgebouwd.
 */
export function LuieTooltip({ title, children, enterDelay = 250, ...rest }) {
  const [anker, setAnker] = useState(null);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const id = useId();
  useEffect(() => () => clearTimeout(timer.current), []);
  const kind = React.Children.only(children);
  const keten = (naam, eigen) => (e) => { kind.props[naam]?.(e); eigen(e); };
  const toon = (e, wacht) => {
    const el = e.currentTarget;
    setAnker((a) => (a === el ? a : el));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), wacht);
  };
  const sluit = () => { clearTimeout(timer.current); setOpen(false); };
  const inhoud = open ? (typeof title === 'function' ? title() : title) : '';
  const zichtbaar = open && Boolean(inhoud);
  // Toegankelijk: het echte blok verwijst naar de tooltip (naast wat het al had, bv. dnd-kit).
  const beschreven = [kind.props['aria-describedby'], zichtbaar ? id : null].filter(Boolean).join(' ') || undefined;
  const el = React.cloneElement(kind, {
    'aria-describedby': beschreven,
    onMouseEnter: keten('onMouseEnter', (e) => toon(e, enterDelay)),
    onMouseLeave: keten('onMouseLeave', sluit),
    onFocus: keten('onFocus', (e) => toon(e, 0)),
    onBlur: keten('onBlur', sluit),
    // Slepen begint met pointerdown (dnd-kit): dan geen tooltip die meereist.
    onPointerDown: keten('onPointerDown', sluit),
  });
  return (
    <>
      {el}
      {anker && (
        <Tooltip
          id={id}
          describeChild
          title={inhoud || ''}
          open={zichtbaar}
          onClose={sluit}
          disableHoverListener disableFocusListener disableTouchListener
          PopperProps={{ anchorEl: anker }}
          {...rest}
        >
          <span hidden />
        </Tooltip>
      )}
    </>
  );
}

// ─── Datumkop ────────────────────────────────────────────────────────────────

export function DatumKop({ kolommen, sjabloon, links, naamBreedte = NAAM_BREEDTE }) {
  const vandaag = vandaagISO();
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: `${naamBreedte}px ${sjabloon}`,
      borderBottom: `1px solid ${RAND}`, bgcolor: '#fff',
    }}>
      <Box sx={{ px: 1, py: 0.5, borderRight: `1px solid ${RAND}`, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {links}
      </Box>
      {kolommen.map((k) => {
        const isVandaag = k.iso === vandaag;
        if (k.weekend) {
          return <Box key={k.iso} title={`${k.dagNaam} ${k.dagNr} ${k.maand} — weekend`} sx={{ background: ARCERING, borderLeft: kolomRand(k) }} />;
        }
        return (
          <Box key={k.iso} sx={{
            borderLeft: kolomRand(k), textAlign: 'center', py: 0.4, minWidth: 0, overflow: 'hidden',
            boxShadow: isVandaag ? `inset 0 -2px 0 ${TEKST}` : 'none',
          }}>
            <Typography noWrap sx={{ fontSize: 10.5, lineHeight: 1.2, color: GEDEMPT, fontWeight: 500 }}>
              {k.maandag ? `wk ${k.weekNr}` : k.eersteVanMaand ? k.maand : k.dagNaam}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, lineHeight: 1.3, fontWeight: isVandaag ? 800 : 600, color: TEKST }}>
              {k.maandag || k.eersteVanMaand ? `${k.dagNaam} ${k.dagNr}` : k.dagNr}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Balken en blokken (sleepbaar) ───────────────────────────────────────────

function datumTekst(task) {
  const s = String(task.startDate || '').slice(0, 10);
  const e = String(task.endDate || '').slice(0, 10);
  return s ? `${dagDatum(s) || s}${e && e !== s ? ` t/m ${dagDatum(e) || e}` : ''}` : '';
}

const eindVan = (t) => String(t.endDate || t.startDate || '').slice(0, 10);

function balkTooltip(task, ongepland) {
  const { val, estimated } = effectiveTaskHours(task, DEFAULT_CAPACITY_RULES);
  const dagen = werkdagenVoor(task);
  const dl = task.deadline ? daysUntil(task.deadline) : null;
  // Project voluit (nummer · klant — projectnaam) en de PL: in het blok zelf staat de
  // projectnaam ingekort (wiki Rooster 27 sep).
  const project = taskProjectVoluit(task);
  const pl = taskPlTekst(task);
  const s = String(task.startDate || '').slice(0, 10);
  return (
    <Box sx={{ maxWidth: 320 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>{task.name}</Typography>
      {project && <Typography sx={{ fontSize: 12, opacity: 0.85 }}>{project}</Typography>}
      {pl && <Typography sx={{ fontSize: 12, opacity: 0.85 }}>{pl}</Typography>}
      <Typography sx={{ fontSize: 12, mt: 0.5 }}>
        {task.type || 'Taak'} · {estimated ? `~${fmtHalfUren(val)}u (geschat)` : `${fmtHalfUren(val)}u`} · {dagen} werkdag{dagen > 1 ? 'en' : ''}
      </Typography>
      <Typography sx={{ fontSize: 12 }}>
        {ongepland ? (s ? `Gewenste start ${s} (buiten beeld)` : 'Nog geen startdatum') : datumTekst(task)}
      </Typography>
      {dl !== null && (
        <Typography sx={{ fontSize: 12, color: dl <= 3 ? '#fca5a5' : '#c7d2fe' }}>
          Deadline {dagDatum(task.deadline) || String(task.deadline).slice(0, 10)} {dl < 0 ? '(verlopen)' : dl === 0 ? '(vandaag)' : `(over ${dl} d)`}
        </Typography>
      )}
    </Box>
  );
}

export function blokTooltip(blok, label, uren, rood) {
  if (blok.tasks.length === 1) {
    return (
      <>
        {balkTooltip(blok.task, false)}
        {rood && <Typography sx={{ fontSize: 12, mt: 0.5, color: '#fca5a5', fontWeight: 700 }}>Loopt over een overboekte dag</Typography>}
        <Typography sx={{ fontSize: 10.5, mt: 0.5, fontStyle: 'italic' }}>Klik voor details en bewerken</Typography>
        {blok.uitgeklaptUit
          ? <Typography sx={{ fontSize: 10.5, fontStyle: 'italic' }}>Sleep om alleen deze taak te verplaatsen · ⤡ of Esc voegt de taken weer samen</Typography>
          : <Typography sx={{ fontSize: 10.5, fontStyle: 'italic' }}>Sleep om te verplaatsen</Typography>}
      </>
    );
  }
  // Op einddatum: dan lees je van boven naar beneden wat eerst af moet (wiki 26 sep: "zijn
  // die van dezelfde dag, moeten die gelijk af zijn?"). Alles op één dag af = één regel.
  const opVolgorde = [...blok.tasks].sort((a, b) => eindVan(a).localeCompare(eindVan(b)));
  const einden = new Set(opVolgorde.map(eindVan));
  const samenAf = einden.size === 1 && eindVan(opVolgorde[0]);
  const n = blok.tasks.length;
  const projectNaam = taskProjectNaam(blok.task);
  const pl = taskPlTekst(blok.task);
  return (
    <Box sx={{ maxWidth: 340 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Typography>
      {projectNaam && !String(label || '').includes(projectNaam) && <Typography sx={{ fontSize: 12, opacity: 0.85 }}>{projectNaam}</Typography>}
      {pl && <Typography sx={{ fontSize: 12, opacity: 0.85 }}>{pl}</Typography>}
      <Typography sx={{ fontSize: 12, opacity: 0.85, mb: 0.5 }}>{n} taken · {fmtHalfUren(uren)}u</Typography>
      {opVolgorde.map((t) => (
        <Typography key={t.id} sx={{ fontSize: 12, lineHeight: 1.35 }}>
          • {t.name} — {fmtHalfUren(effectiveTaskHours(t, DEFAULT_CAPACITY_RULES).val)}u{samenAf ? '' : `, af ${dagDatum(eindVan(t)) || eindVan(t)}`}
        </Typography>
      ))}
      {samenAf && (
        <Typography sx={{ fontSize: 12, mt: 0.5, fontWeight: 600 }}>
          {n === 2 ? 'Beide' : `Alle ${n}`} af op {dagDatum(samenAf) || samenAf}
        </Typography>
      )}
      {rood && <Typography sx={{ fontSize: 12, mt: 0.5, color: '#fca5a5', fontWeight: 700 }}>Loopt over een overboekte dag</Typography>}
      <Typography sx={{ fontSize: 10.5, mt: 0.5, fontStyle: 'italic' }}>Klik om de taken los te zien</Typography>
      <Typography sx={{ fontSize: 10.5, fontStyle: 'italic' }}>Sleep om ze samen te verschuiven</Typography>
    </Box>
  );
}

/**
 * Eén vlak op het rooster (bak-taak of projectblok). Rustig (Joris, 25 sep): een licht
 * pastelvlak met donkere tekst en een dunne streep links in de volle projectkleur — in de
 * bak én op de rij. Overboeking staat als rood getal onderin de dag, niet op het blok.
 */
const Balk = React.forwardRef(function Balk({
  c0, c1, lane, stijl, logo, regel1, regel2, onder, uren, actie, afgekaptLinks, afgekaptRechts, isDragging, rand, sx, children, ...rest
}, ref) {
  return (
    <Box
      ref={ref}
      data-lane={lane}
      {...rest}
      sx={{
        gridColumn: `${c0 + 2} / ${c1 + 3}`,
        gridRow: `${lane + 1}`,
        zIndex: 2, mx: '2px', minWidth: 0, height: LANE_H,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        px: 0.75, py: '2px', position: 'relative',
        borderRadius: afgekaptLinks ? '0 6px 6px 0' : afgekaptRechts ? '6px 0 0 6px' : '6px',
        bgcolor: stijl.vlak,
        color: stijl.tekst, cursor: 'grab',
        opacity: isDragging ? 0.35 : 1,
        boxShadow: `inset 3px 0 0 ${stijl.streep}${rand ? `, inset 0 0 0 1px ${rand}` : ''}`,
        '&:hover': { filter: 'brightness(0.97)' },
        '&:active': { cursor: 'grabbing' },
        touchAction: 'none', overflow: 'hidden',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
        {/* Eigen plek in de rij (niet inline in de afgekapte tekst): zo knipt overflow het logo nooit af. */}
        {logo}
        <Typography sx={{
          fontSize: 12, fontWeight: 600, lineHeight: 1.18, flex: 1, minWidth: 0,
          display: '-webkit-box', WebkitLineClamp: regel2 ? 1 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word',
        }}>
          {regel1}
        </Typography>
        <Typography sx={{ fontSize: 10.5, fontWeight: 600, lineHeight: 1.3, flexShrink: 0, opacity: 0.8 }}>{uren}</Typography>
        {actie}
      </Box>
      {onder || (regel2 && (
        <Typography noWrap sx={{ fontSize: 10.5, lineHeight: 1.25, opacity: 0.8, minWidth: 0 }}>{regel2}</Typography>
      ))}
      {children}
    </Box>
  );
});

/**
 * De onderregel van een projectblok in stukken per einddatum (zie blokSegmenten): elk stuk
 * staat onder zijn eigen dagen, met een klein ruitje (deadline-markering) op de dag waarop de
 * taak af moet (wiki Rooster 27 sep: het streepje las niet als deadline). Geen eigen kleur,
 * geen eigen sleepgedrag — het blok blijft één geheel; pointer-events gaan erdoorheen.
 */
function SegmentStrook({ segmenten, blok, sjabloon, streep }) {
  const kolommen = sjabloonDelen(sjabloon).slice(blok.c0, blok.c1 + 1);
  if (!kolommen.length) return null;
  return (
    <Box
      data-testid="blok-segmenten"
      sx={{
        // Tegen de binnenrand van de balk in (px 0.75 = 6px), zodat de stukken op de dagkolommen vallen.
        mx: '-6px', display: 'grid', gridTemplateColumns: kolommen.join(' '),
        height: 13, pointerEvents: 'none', minWidth: 0,
      }}
    >
      {segmenten.map((seg, i) => (
        <Box
          key={`${seg.c0}-${seg.c1}`}
          title={seg.label}
          sx={{
            gridColumn: `${seg.c0 - blok.c0 + 1} / ${seg.c1 - blok.c0 + 2}`,
            minWidth: 0, px: '6px', display: 'flex', alignItems: 'center', position: 'relative',
          }}
        >
          <Typography noWrap sx={{ fontSize: 10.5, lineHeight: 1.2, opacity: 0.8, minWidth: 0 }}>{seg.label}</Typography>
          {i < segmenten.length - 1 && <DeadlineRuit eind={seg.eind} kleur={streep} />}
        </Box>
      ))}
    </Box>
  );
}

/** Klein ruitje op de grens van een segment: hier moet die taak af. Rustig: 5 px, projecttint. */
function DeadlineRuit({ eind, kleur }) {
  const tekst = `af ${dagDatum(eind) || eind}`;
  return (
    <Box
      data-testid="deadline-ruit" role="img" aria-label={tekst} title={tekst}
      sx={{
        position: 'absolute', right: '-3px', top: '50%', width: 5, height: 5,
        transform: 'translateY(-50%) rotate(45deg)', bgcolor: tint(kleur, 0.8), borderRadius: '1px',
      }}
    />
  );
}

/**
 * Dagstart (Joris 7 okt): de mijlpalen van een projectblok. Elk stuk staat onder de dagen van
 * zijn taak; rechts in dat stuk staat de versie van díe taak met het ruitje er direct achter,
 * precies op de einddag ("v intern ◆"). Zo hoort label + ruitje onmiskenbaar bij één taak en
 * leest het volgende label niet meer als het label van het vorige ruitje. v1 is de
 * hoofdmijlpaal: label als accentchip en een groter ruitje in dezelfde kleur; de rest gedempt.
 * `links` (optioneel, één stuk): de gewone onderregel links ervan.
 */
export function MijlpaalStrook({ segmenten, blok, sjabloon, links = null }) {
  const kolommen = sjabloonDelen(sjabloon).slice(blok.c0, blok.c1 + 1);
  if (!kolommen.length) return null;
  return (
    <Box
      data-testid="blok-mijlpalen"
      sx={{
        mx: '-6px', display: 'grid', gridTemplateColumns: kolommen.join(' '),
        height: 14, pointerEvents: 'none', minWidth: 0,
      }}
    >
      {segmenten.map((seg, i) => (
        <Box
          key={`${seg.c0}-${seg.c1}`}
          data-testid="mijlpaal"
          data-versie={seg.versie || ''}
          data-hoofd={seg.hoofd ? 'ja' : 'nee'}
          sx={{
            gridColumn: `${seg.c0 - blok.c0 + 1} / ${seg.c1 - blok.c0 + 2}`,
            // overflow hidden: een label tekent nooit buiten zijn eigen stuk (over het ruitje
            // van de vorige taak); te smal = het label kort af, het ruitje blijft altijd staan.
            minWidth: 0, px: '6px', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px',
          }}
        >
          {links && i === 0 && (
            <Typography noWrap sx={{ fontSize: 10.5, lineHeight: 1.2, opacity: 0.8, minWidth: 0, flex: '1 1 0' }}>{links}</Typography>
          )}
          <MijlpaalLabel seg={seg} />
          {seg.ruit && <MijlpaalRuit seg={seg} />}
        </Box>
      ))}
    </Box>
  );
}

/** Vorm van elke mijlpaal-badge: gevuld, afgerond, één regel. */
const BADGE_VORM = {
  flexShrink: 1, minWidth: 0, px: '4px', borderRadius: '4px',
  fontSize: 10, fontWeight: 700, lineHeight: '14px', whiteSpace: 'nowrap',
  overflow: 'hidden', textOverflow: 'ellipsis',
};

/**
 * Eén weergave voor elke mijlpaal (3 nov: "verschillende v1- en DEF-icoontjes"): losse blokken,
 * samengevoegde blokken en deliverables tonen dezelfde badge. Met een versie: de gekleurde
 * badge met alleen de versie, en de naam (deliverable/taak) klein ervoor; zonder versie: de naam
 * in een neutrale badge.
 */
export function MijlpaalLabel({ seg }) {
  const badge = seg.badge || (seg.hoofd ? { soort: 'v1', kleur: V1_ACCENT } : null);
  if (badge && seg.versie) {
    const naam = mijlpaalNaam(seg.label, seg.versie);
    return (
      <Box component="span" data-testid="mijlpaal-label" title={seg.label}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', minWidth: 0, flexShrink: 1 }}>
        {naam && (
          <Typography component="span" noWrap data-testid="mijlpaal-naam"
            sx={{ fontSize: 10.5, lineHeight: 1.2, color: '#4b5563', minWidth: 0, flexShrink: 1 }}>
            {naam}
          </Typography>
        )}
        <Box component="span" data-badge={badge.soort} sx={{ ...BADGE_VORM, flexShrink: 0, bgcolor: badge.kleur, color: '#fff' }}>
          {seg.versie}
        </Box>
      </Box>
    );
  }
  // Stuk zonder versie in een blok mét versies: geen label (3 nov: alleen v intern, v1, v2 … en
  // DEF zijn mijlpalen); het kleine grijze ruitje markeert nog wel de deadline van die taak.
  if (!seg.versie) return null;
  // Subversie zonder eigen badge (v1.2): dezelfde vorm, rustig neutraal.
  return (
    <Box component="span" data-testid="mijlpaal-label" data-badge="neutraal" title={seg.label}
      sx={{ ...BADGE_VORM, bgcolor: '#e5e7eb', color: '#374151' }}>
      {seg.versie}
    </Box>
  );
}

/** Ruitje precies op de einddag van de taak: v1 groot en in het accent, de rest klein en gedempt. */
function MijlpaalRuit({ seg }) {
  const badge = seg.badge || (seg.hoofd ? { soort: 'v1', kleur: V1_ACCENT } : null);
  const d = badge && (badge.soort === 'def' || badge.soort === 'v1') ? 8 : badge ? 7 : 6;
  const tekst = (seg.deadlines?.length ? seg.deadlines : [{ naam: seg.versie || seg.label, eind: seg.eind }])
    .map((d) => `${d.naam} af ${dagDatum(d.eind) || d.eind}`).join(' · ');
  return (
    <Box
      data-testid="deadline-ruit" role="img" aria-label={tekst} title={tekst}
      sx={{
        // In de stroom, als laatste element van het stuk: rechts op de einddag, nooit
        // afgekapt door de rand van de balk en nooit vóór het label van de volgende taak.
        flexShrink: 0, width: d, height: d, mx: '1px',
        transform: 'rotate(45deg)', borderRadius: '1px',
        bgcolor: badge ? badge.kleur : MIJLPAAL_GEDEMPT,
      }}
    />
  );
}

const MND = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const korteDatum = (iso) => { const [, m, d] = String(iso).slice(0, 10).split('-').map(Number); return m && d ? `${d} ${MND[m - 1]}` : ''; };

/**
 * "Klant reviewt" (3 nov): rustig gearceerd vlak in de kleur van het werk, op de lane van de
 * versie ervoor, tussen het einde van die versie en de start van de volgende (of het moment dat
 * de feedback compleet is). Geen taak: niet klikbaar, niet sleepbaar, geen uren.
 */
export function ReviewArcering({ seg }) {
  const kleur = werkSoortStijl(seg.taak).streep;
  const project = seg.taak ? (taskProjectLabel(seg.taak) || seg.taak.projectName || '') : '';
  const tekst = seg.compleet
    ? `Klant reviewde ${seg.van} — feedback compleet op ${dagDatum(seg.compleet) || seg.compleet}`
    : `Klant reviewt ${seg.van} — feedback verwacht vóór ${dagDatum(seg.tot) || seg.tot} (start ${seg.naar})`;
  const titel = project ? `${tekst} · ${project}` : tekst;
  return (
    <Box
      data-testid="klant-reviewt" role="img" aria-label={titel} title={titel}
      sx={{
        gridColumn: `${seg.c0 + 2} / ${seg.c1 + 3}`, gridRow: `${seg.lane + 1}`,
        zIndex: 1, mx: '2px', height: LANE_H, borderRadius: '6px', alignSelf: 'center',
        background: `repeating-linear-gradient(135deg, ${tint(kleur, 0.08)} 0 5px, ${tint(kleur, 0.24)} 5px 10px)`,
        boxShadow: `inset 0 0 0 1px ${tint(kleur, 0.3)}`,
        display: 'flex', alignItems: 'center', px: 0.75, minWidth: 0, overflow: 'hidden',
      }}
    >
      {/* 3 nov: een kaal gearceerd vlak las als "leeg blok"; nu staat erin wát er gebeurt, en
          (Joris: "nog niet duidelijk bij welk project") van wélk project: logo + nummer · klant. */}
      <Box component="span" aria-hidden="true" data-testid="klant-reviewt-label"
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0, maxWidth: '100%',
          px: '5px', py: '1px', borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.92)',
        }}>
        <KlantLogo url={seg.taak?.klantLogo} klant={seg.taak?.client} maat={14} />
        <Typography component="span" noWrap sx={{ fontSize: 10.5, lineHeight: '16px', color: '#374151', minWidth: 0 }}>
          {project && <Box component="span" sx={{ fontWeight: 700 }}>{project} · </Box>}
          {seg.compleet ? `feedback ${seg.van} compleet` : `klant reviewt ${seg.van}`}
          {!seg.compleet && seg.naar && <Box component="span" sx={{ opacity: 0.75 }}> → {seg.naar}</Box>}
        </Typography>
      </Box>
    </Box>
  );
}

/** Kolommen van een blok waarop de persoon afwezig is (werkdagen), of null als er geen zijn. */
export function afwezigInBlok(blok, kolommen, staat) {
  const uit = new Set();
  for (let i = blok.c0; i <= blok.c1; i += 1) {
    const k = kolommen?.[i];
    if (k && !k.weekend && staat?.[k.iso] === 'vrij') uit.add(i);
  }
  return uit.size ? uit : null;
}

/**
 * Gepland op een afwezige dag (Joris, 3 nov): dat deel van de balk wordt lichter, met onderaan
 * een smalle arcering. Een laag binnen de balk (z-index -1 in de stacking context van de balk), dus
 * de tekst blijft leesbaar en klikken/slepen gaan gewoon naar de balk.
 */
function AfwezigLaag({ blok, sjabloon, afwezig }) {
  const delen = sjabloonDelen(sjabloon).slice(blok.c0, blok.c1 + 1);
  if (!delen.length) return null;
  return (
    <Box aria-hidden="true" data-testid="afwezig-in-blok" sx={{
      // De balk heeft mx 2px: de laag loopt 2px over de randen (de balk knipt af), zodat het
      // grid dezelfde breedte heeft als de dagkolommen en de arcering precies op de dag valt.
      position: 'absolute', top: 0, bottom: 0, left: '-2px', right: '-2px', zIndex: -1, pointerEvents: 'none',
      display: 'grid', gridTemplateColumns: delen.join(' '),
    }}>
      {delen.map((_, n) => (
        <Box key={n} data-afwezig={afwezig.has(blok.c0 + n) ? 'ja' : undefined} sx={afwezig.has(blok.c0 + n) ? {
          // 3 nov ("zichtbaarheid in het gearceerde"): geen strepen door de tekst. Egaal lichter
          // vlak (de dag is "niet echt"), alleen onderaan een smalle arcering als signaal.
          background: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.95) 0 3px, transparent 3px 6px) left bottom / 100% 5px no-repeat, rgba(255,255,255,0.5)',
        } : undefined} />
      ))}
    </Box>
  );
}

/** Taak in de "te plannen"-bak: volledige naam (twee regels), uren rechtsboven. */
export function TaakBalk({ plek, onClick, klikHint }) {
  const { task } = plek;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task-${task.id}`,
    data: { type: 'task-bar', task },
  });
  const { val, estimated } = effectiveTaskHours(task, DEFAULT_CAPACITY_RULES);
  return (
    <LuieTooltip title={() => <>{balkTooltip(task, plek.ongepland)}{klikHint && <Typography sx={{ fontSize: 10.5, mt: 0.5, fontStyle: 'italic' }}>{klikHint}</Typography>}</>} arrow disableInteractive enterDelay={250} placement="top">
      <Balk
        ref={setNodeRef} {...listeners} {...attributes} onClick={onClick}
        c0={plek.c0} c1={plek.c1} lane={plek.lane} stijl={projectStijl(task)}
        regel1={task.name} uren={`${estimated ? '~' : ''}${fmtHalfUren(val)}u`}
        regel2={plek.ongepland ? (task.startDate ? `wens ${korteDatum(task.startDate)}` : 'nog geen datum') : undefined}
        afgekaptLinks={plek.afgekaptLinks} afgekaptRechts={plek.afgekaptRechts}
        isDragging={isDragging}
      />
    </LuieTooltip>
  );
}

/**
 * Projectblok op een persoonsrij: project bovenaan, taak/taken eronder, uren rechts.
 * Klik op één taak (ook een losgeklapte) opent het TaakPaneel (`onOpen`). Een blok met
 * meerdere taken klapt met een klik uit tot losse, apart sleepbare taken (`onKlik`); de
 * meest linkse losse taak draagt het samenvoegicoon ⤡ dat ze weer inklapt (Esc doet dat
 * ook, zie PersoonRij).
 * `onRand(event, kant, taak, blok)` (optioneel, alleen Capaciteit): een blok met één taak krijgt
 * de randhandvatten van de Dagstart — rand slepen = korter/langer. Weggelaten (Rooster,
 * Dagstart-rooster) = geen handvatten, alles zoals het was.
 */
export function ProjectBlok({ blok, bronPersoonId, rood, onKlik, onOpen, kolommen, sjabloon, onRand, betekenis = false, afwezig = null }) {
  const enkel = blok.tasks.length === 1;
  const los = !!blok.uitgeklaptUit;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: enkel ? `rij-${bronPersoonId}-${blok.task.id}` : `blok-${bronPersoonId}-${blok.id}`,
    data: enkel
      ? { type: 'task-bar', task: { ...blok.task, bronPersoonId } }
      : { type: 'blok', tasks: blok.tasks, bronPersoonId, label: taskProjectLabel(blok.task) || blok.task.projectName },
  });
  const uren = urenVan(blok.tasks);
  const geschat = blok.tasks.some((t) => effectiveTaskHours(t, DEFAULT_CAPACITY_RULES).estimated);
  const isDraaidag = blok.task.type === 'Draaidag';
  // Dagstart (`betekenis`): kleur = soort werk in plaats van een kleur per project.
  const stijl = betekenis ? werkSoortStijl(blok.tasks) : projectStijl(blok.task);
  const project = taskProjectLabel(blok.task) || blok.task.projectName || '';
  const label = los
    ? kortTaak(blok.task.name)
    : isDraaidag ? `Draaidag${project ? ` · ${project}` : ''}` : (project || blok.task.name);
  // Onderregel "projectnaam · taak" (wiki Rooster 27 sep: alleen "9736 · Enexis" zei niet
  // wélk project); de projectnaam niet dubbel als de taaknaam hem al bevat.
  // De dubbelcheck gaat tegen de tekst die je écht ziet (bij losse/samengevoegde blokken het
  // korte taaklabel), niet tegen de volledige taaknaam.
  const takenTekst = `${blok.tasks.length} taken · ${[...new Set(blok.tasks.map((t) => kortTaak(t.name)))].join(', ')}`;
  const regel2 = los
    ? [project, taskProjectNaamKort(blok.task, { namen: [label], max: NAAM_MAX })].filter(Boolean).join(' · ')
    : project || isDraaidag
      ? (enkel
        ? taskProjectTaakRegel(blok.task, { max: NAAM_MAX })
        : taskProjectTaakRegel(blok.task, { tekst: takenTekst, namen: [takenTekst], max: NAAM_MAX }))
      : (enkel ? '' : `${blok.tasks.length} taken`);
  // Meerdere taken met verschillende einddatums: de onderregel in stukken per taak.
  const segmenten = !enkel && !los && kolommen && sjabloon && !betekenis ? blokSegmenten(blok, kolommen) : [];
  // Dagstart: mijlpalen (ruitje aan het einde van elke taak, met de versie van díe taak).
  const mijlpalen = betekenis && !isDraaidag && kolommen && sjabloon ? mijlpaalSegmenten(blok, kolommen) : [];
  // Meerdere stukken: de strook is de onderregel. Eén stuk (één versie): de gewone onderregel
  // links, de mijlpaal rechts.
  const strookIsRegel = mijlpalen.length > 1;
  // Met segmenten is de onderregel de strook met taaklabels; de projectnaam schuift dan
  // gedempt achter "nummer · klant" op de bovenregel, zodat hij niet alleen in de hover staat.
  const naamBoven = (segmenten.length > 0 || strookIsRegel) && !isDraaidag
    ? taskProjectNaamKort(blok.task, { namen: [(strookIsRegel ? mijlpalen : segmenten).map((s) => s.label).join(' ')], max: NAAM_MAX })
    : '';
  // Dagstart (3 nov): klantlogo vóór de projectnaam, zoals in Airtable; zonder logo de initialen.
  const logo = betekenis && project && !isDraaidag
    ? <KlantLogo url={blok.task.klantLogo} klant={blok.task.client} maat={16} />
    : null;
  const regel1 = naamBoven
    ? <>{label}<Box component="span" sx={{ fontWeight: 400, opacity: 0.75 }}> · {naamBoven}</Box></>
    : label;
  // Eén taak (ook een losgeklapte): klik opent het TaakPaneel. Meerdere taken: klik klapt
  // uit. Zonder paneel (geen onOpen) klapt een losse taak met een klik weer in, zoals vroeger.
  // Slepen blijft werken: de sensor start pas na 8 px beweging, een klik is dus nooit een sleep.
  const opent = Boolean(onOpen) && enkel;
  const klikbaar = Boolean(onKlik) && (!enkel || (los && !onOpen));
  // Randen alleen waar het blok echt begint/eindigt (niet op een afgekapte kant).
  const randen = onRand && enkel && !isDraaidag;
  const klik = (e) => {
    // Loslaten na rand slepen is geen klik op het blok.
    if (e.target?.classList?.contains('dagstart-resize')) { e.stopPropagation(); return; }
    if (opent) { e.stopPropagation(); onOpen(blok.task); return; }
    if (klikbaar) { e.stopPropagation(); onKlik(blok); }
  };
  // Samenvoegen: een klein icoon op de eerste losse taak van de groep. Alleen voor de muis
  // (geen tweede knop ín de sleepbare balk); met het toetsenbord klapt Esc in.
  const samenvoegen = los && onKlik && blok.eersteUitgeklapt ? (
    <Box
      component="span" aria-hidden="true" title="Samenvoegen"
      data-testid="samenvoegen"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onKlik(blok); }}
      sx={{
        flexShrink: 0, fontSize: 12, lineHeight: 1, px: '3px', py: '1px', mt: '-1px', borderRadius: '4px',
        cursor: 'pointer', opacity: 0.7, '&:hover, &:focus-visible': { opacity: 1, bgcolor: 'rgba(0,0,0,0.08)' },
      }}
    >
      ⤡
    </Box>
  ) : null;
  return (
    <LuieTooltip title={() => blokTooltip(blok, label, uren, rood)} arrow disableInteractive enterDelay={250} placement="top">
      <Balk
        ref={setNodeRef} {...listeners} {...attributes}
        onClick={klikbaar || opent ? klik : undefined}
        onKeyDown={klikbaar || opent ? (e) => { if (e.key === 'Enter') { e.preventDefault(); klik(e); } } : undefined}
        aria-expanded={onKlik && !enkel ? false : undefined}
        actie={samenvoegen}
        data-taken={blok.tasks.map((t) => t.id).join(' ')}
        c0={blok.c0} c1={blok.c1} lane={blok.lane} stijl={stijl} logo={logo}
        regel1={regel1} regel2={regel2} uren={`${geschat ? '~' : ''}${fmtHalfUren(uren)}u`}
        onder={mijlpalen.length
          ? <MijlpaalStrook segmenten={mijlpalen} blok={blok} sjabloon={sjabloon} links={strookIsRegel ? null : regel2} />
          : segmenten.length ? <SegmentStrook segmenten={segmenten} blok={blok} sjabloon={sjabloon} streep={stijl.streep} /> : null}
        afgekaptLinks={blok.afgekaptLinks} afgekaptRechts={blok.afgekaptRechts}
        isDragging={isDragging}
        // Uitgeklapt: zelfde pastel, met een dunne rand in de projecttint, zodat zichtbaar
        // blijft dat de losse taken bij elkaar horen.
        rand={los ? tint(stijl.streep, 0.35) : undefined}
        sx={randen ? { '&:hover .dagstart-resize': { opacity: 0.55 } } : undefined}
      >
        {afwezig && sjabloon && <AfwezigLaag blok={blok} sjabloon={sjabloon} afwezig={afwezig} />}
        {randen && !blok.afgekaptLinks && <ResizeHandvat kant="left" onPointerDown={(e) => onRand(e, 'links', blok.task, blok)} />}
        {randen && !blok.afgekaptRechts && <ResizeHandvat kant="right" onPointerDown={(e) => onRand(e, 'rechts', blok.task, blok)} />}
      </Balk>
    </LuieTooltip>
  );
}

// Vast blok: draaidag (uit de draaidagenbron) of een afspraak van ≥ 3 u. Geen taak, dus
// niet sleepbaar en zonder projectkleur: een stil grijs vlak met donkergrijze tekst. Een
// optie is gestreept en lichter — vastgehouden, nog niet bevestigd.
const VAST_VLAK = '#e5e7eb';
const VAST_TEKST = '#374151';
// Draaidagen: oranje zoals in Airtable (wiki Dagstart 26 sep), in dezelfde pastelsterkte als een
// projectblok met een dunne oranje streep links. Een optie (titel begint met "OPTIE") is
// hetzelfde oranje maar gearceerd en lichter: vastgehouden, nog niet bevestigd.
export const DRAAIDAG_VLAK = tint(DRAAIDAG_ORANJE, 0.14);
export const DRAAIDAG_OPTIE_VLAK = `repeating-linear-gradient(135deg, ${tint(DRAAIDAG_ORANJE, 0.06)} 0 6px, ${tint(DRAAIDAG_ORANJE, 0.16)} 6px 12px)`;

/** Achtergrond, tekst en streep van een vast blok (draaidag, optie-draaidag of afspraak). */
export function vastStijl(t) {
  if (t?.bronDraaidag) {
    return t.draaidagOptie
      ? { background: DRAAIDAG_OPTIE_VLAK, color: GEDEMPT, boxShadow: `inset 3px 0 0 ${tint(DRAAIDAG_ORANJE, 0.55)}` }
      : { background: DRAAIDAG_VLAK, color: TEKST, boxShadow: `inset 3px 0 0 ${DRAAIDAG_ORANJE}` };
  }
  return { background: VAST_VLAK, color: VAST_TEKST, boxShadow: 'none' };
}

function vastTooltip(blok) {
  const t = blok.task;
  if (t.bronDraaidag) {
    const crew = (t.draaidagCrew || []).join(', ');
    return (
      <Box sx={{ maxWidth: 340 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{t.name}</Typography>
        {t.draaidagTitel && <Typography sx={{ fontSize: 12, opacity: 0.85 }}>{t.draaidagTitel}</Typography>}
        <Typography sx={{ fontSize: 12, mt: 0.5 }}>{datumTekst(t)}{t.draaidagOptie ? ' · optie (nog niet bevestigd)' : ''}</Typography>
        {crew && <Typography sx={{ fontSize: 12 }}>Crew: {crew}</Typography>}
        <Typography sx={{ fontSize: 10.5, mt: 0.5, fontStyle: 'italic' }}>Uit de draaidagen — volle dag bezet, niet te verslepen</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ maxWidth: 340 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{t.name}</Typography>
      {blok.tasks.map((x) => (
        <Typography key={x.id} sx={{ fontSize: 12 }}>
          {korteDatum(x.startDate)}{x.tijd ? ` · ${x.tijd}` : ''} · {fmtHalfUren(x.hours)}u
        </Typography>
      ))}
      <Typography sx={{ fontSize: 10.5, mt: 0.5, fontStyle: 'italic' }}>Afspraak in Outlook — daar aanpassen</Typography>
    </Box>
  );
}

export function VastBlok({ blok, onOpen }) {
  const t = blok.task;
  const uren = t.bronAgenda ? blok.tasks.reduce((s, x) => s + (Number(x.hours) || 0), 0) : 0;
  // Dagstart (3 nov): een klik opent het detailscherm van de draaidag of de afspraak. Niet
  // sleepbaar, dus een gewone klik; zonder onOpen (Planning → Rooster) blijft het stil.
  const open = onOpen ? () => onOpen(t, blok) : undefined;
  return (
    <LuieTooltip title={() => vastTooltip(blok)} arrow disableInteractive enterDelay={250} placement="top">
      <Box
        data-testid={t.bronDraaidag ? 'draaidag-blok' : 'agenda-blok'}
        onClick={open}
        onKeyDown={open ? (e) => { if (e.key === 'Enter') { e.preventDefault(); open(); } } : undefined}
        role={open ? 'button' : undefined}
        tabIndex={open ? 0 : undefined}
        aria-label={open ? `${t.name} openen` : undefined}
        sx={{
          gridColumn: `${blok.c0 + 2} / ${blok.c1 + 3}`,
          gridRow: `${blok.lane + 1}`,
          zIndex: 2, mx: '2px', minWidth: 0, height: LANE_H,
          display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75,
          borderRadius: blok.afgekaptLinks ? '0 6px 6px 0' : blok.afgekaptRechts ? '6px 0 0 6px' : '6px',
          ...vastStijl(t),
          cursor: open ? 'pointer' : 'default', overflow: 'hidden',
          ...(open && { '&:hover': { filter: 'brightness(0.97)' }, '&:focus-visible': { outline: '2px solid #94a3b8', outlineOffset: 1 } }),
        }}
      >
        <Typography noWrap sx={{ fontSize: 12, fontWeight: t.bronDraaidag ? 600 : 500, lineHeight: 1.2, flex: 1, minWidth: 0 }}>
          {t.name}
        </Typography>
        {uren > 0 && (
          <Typography sx={{ fontSize: 10.5, fontWeight: 600, lineHeight: 1.3, flexShrink: 0, opacity: 0.8 }}>{fmtHalfUren(uren)}u</Typography>
        )}
      </Box>
    </LuieTooltip>
  );
}

// ─── Dagcel (drop-doel) + dagsignaal ─────────────────────────────────────────

/** Hover-tekst van een dag: bezetting, en wat de dag naast projectwerk vult (afspraken met onderwerp). */
function dagTitel({ k, staat, uren, hoursPerDay, dag, afspraken, extra }) {
  const stijl = DAGSTAAT_STIJL[staat];
  if (stijl) return `${staatTitel(staat)}${extra ? `\n⚠ Er staat ook ${fmtUren(extra)}u ander werk op deze dag` : ''}`;
  const over = uren > hoursPerDay + 0.05;
  const regels = [
    `${k.dagNaam} ${k.dagNr} ${k.maand}: ${fmtUren(uren)}u van ${hoursPerDay}u`
      + (over ? ` — ${fmtUren(uren - hoursPerDay)}u te veel` : ` — ${fmtUren(Math.max(0, hoursPerDay - uren))}u vrij`),
  ];
  if (dag && dag.agenda > 0.05) {
    regels.push(`Afspraken ${fmtUren(dag.agenda)}u`);
    (afspraken || []).forEach((ev) => regels.push(
      `• ${ev.start_time && ev.end_time ? `${ev.start_time}–${ev.end_time} ` : ''}${ev.afgeschermd ? 'Afspraak' : (ev.subject || 'Afspraak')} (${fmtHalfUren(ev.hours)}u)`,
    ));
  }
  if (dag && dag.klein > 0.05) {
    regels.push(`Klein werk ${fmtUren(dag.klein)}u`);
    dag.taken.forEach((t) => regels.push(`• ${t.label} (${fmtHalfUren(t.uren)}u)`));
  }
  return regels.join('\n');
}

function DagCel({ personId, k, idx, staat, isOver, aandeel, titel, pad = RIJ_PAD.compact, zonderVulling = false }) {
  const { setNodeRef } = useDroppable({
    id: `cell-${personId}-${k.iso}`,
    data: { type: 'capacity-cell', personId, date: k.iso },
    // Op een afwezige dag (of in het weekend) is iemand er niet: geen drop.
    disabled: staat === 'vrij' || k.weekend,
  });
  // Ruimte = wit. Afspraken/klein werk = grijze vulling van onderen (hoogte = deel van de dag);
  // niet-werkdag = neutraal vlak. Overboekt krijgt géén vlak meer (rust, 25 sep): het rode
  // tekort onderin de dag (DagSignaal) zegt het al.
  // Dagstart (Joris, 3 nov: "het grijze gedeelte mag weg, wel die uurtjes"): geen grijze
  // vulling en geen grijs vlak op een afwezige dag; het dagsaldo ("+3u", "Afwezig") zegt het.
  let achtergrond = '#fff';
  if (k.weekend || (staat && !zonderVulling)) achtergrond = NEUTRAAL;
  else if (aandeel > 0.01 && !zonderVulling) {
    const pct = Math.round(Math.min(1, aandeel) * 100);
    achtergrond = `linear-gradient(to top, ${VULLING} ${pct}%, #fff ${pct}%)`;
  }
  return (
    <Box
      ref={k.weekend ? undefined : setNodeRef}
      title={k.weekend ? undefined : titel}
      data-vulling={aandeel > 0.01 && !zonderVulling ? Math.round(Math.min(1, aandeel) * 100) : undefined}
      sx={{
        gridColumn: `${idx + 2}`,
        gridRow: '1 / -1',
        mt: `-${pad.boven}px`,
        mb: `-${pad.onder}px`,
        borderLeft: kolomRand(k),
        background: isOver ? '#dbeafe' : achtergrond,
        boxShadow: isOver ? 'inset 0 0 0 2px #2563eb' : 'none',
        transition: 'background-color 80ms',
      }}
    />
  );
}

/**
 * Ruimte op een dag: groen (Joris, 3 nov: "mag die +6u groen zijn"), maar gedempt en in
 * gewone dikte, zodat rood (overboekt, vet) de blikvanger blijft.
 */
export const RUIMTE_KLEUR = '#15803d';

/**
 * Eén signaal per dag, onderin: "Afwezig"/"Draaidag" (neutraal, in de Dagstart rood) of het dagtotaal
 * (capaciteit min gepland, zie dagSaldo): bij overboeking het tekort in rood ("−2u"), en met
 * `toonRuimte` (Dagstart) ook de ruimte, gedempt ("+3u"). Onder een kwartier: niets.
 * De hover-tekst begint met de uitleg van het getal, daarna de opbouw van de dag.
 */
function DagSignaal({ k, idx, uren, staat, hoursPerDay, titel, toonRuimte = false }) {
  // Dagstart (Raijmond, 4 nov): "Afwezig" (vrij) of een draaidag valt anders weg tussen het
  // grijs; daar in rood, zodat je ziet wie er niet is.
  const afwezigRood = toonRuimte && (staat === 'vrij' || String(staat || '').startsWith('draaidag'));
  if (k.weekend) return null;
  const stijl = DAGSTAAT_STIJL[staat];
  const saldo = stijl ? null : dagSaldo(uren, hoursPerDay, { toonRuimte });
  if (!stijl && !saldo) return null;
  const over = saldo?.richting === 'over';
  return (
    <Box
      title={saldo ? `${saldo.uitleg}\n\n${titel || ''}`.trim() : titel}
      data-testid={saldo ? `dagsaldo-${saldo.richting}` : undefined}
      sx={{ gridColumn: `${idx + 2}`, gridRow: '-2 / -1', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: stijl ? 'center' : 'flex-end', px: '5px' }}
    >
      <Typography noWrap data-afwezig-rood={afwezigRood ? 'ja' : undefined} sx={{ fontSize: 10.5, fontWeight: afwezigRood ? 700 : stijl ? 500 : over ? 700 : 500, lineHeight: 1, color: afwezigRood ? ROOD : stijl ? GEDEMPT : over ? ROOD : RUIMTE_KLEUR }}>
        {stijl ? staatTekst(staat) : saldo.tekst}
      </Typography>
    </Box>
  );
}

/**
 * Weekendwerk (agenda-afspraak of draaidag op za/zo): een klein grijs markeertje onderin
 * de smalle weekendkolom — nooit op een werkdag, en het telt niet mee in de capaciteit.
 */
function WeekendMarkering({ k, idx, items }) {
  const regels = weekendRegels(k.iso, items);
  return (
    <Tooltip
      arrow disableInteractive enterDelay={150} placement="top"
      title={(
        <Box>
          {regels.map((r) => <Typography key={r} sx={{ fontSize: 12 }}>{r}</Typography>)}
          <Typography sx={{ fontSize: 10.5, mt: 0.5, fontStyle: 'italic' }}>Weekend — telt niet mee in de werkdagcapaciteit</Typography>
        </Box>
      )}
    >
      <Box
        data-testid="weekend-werk"
        aria-label={regels.join('; ')}
        sx={{ gridColumn: `${idx + 2}`, gridRow: '-2 / -1', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}
      >
        <Box sx={{ width: 5, height: 5, maxWidth: '80%', borderRadius: '50%', bgcolor: '#9ca3af' }} />
      </Box>
    </Tooltip>
  );
}

// ─── Persoonsrij ─────────────────────────────────────────────────────────────

/**
 * Ruimte boven/onder een persoonsrij: compact (Rooster) of ruim (Dagstart). Joris 7 okt: de
 * personen liepen met 10/6 px en een haarlijn nog in elkaar over. Nu meer lucht (14/12 px) en
 * tussen twee personen een lijn over de hele breedte (sinds 4 nov smal en donkerder, zie
 * PERSOON_VOEG). Binnen een persoon blijven de lanes compact.
 */
const RIJ_PAD = { compact: { boven: 4, onder: 0 }, ruim: { boven: 14, onder: 12 } };
const RAND_DIKTE = { compact: 1, ruim: 2 };
// De lijn tussen twee personen in de Dagstart. Raijmond (4 nov): de zachte voeg (#eef0f3) had
// bijna de kleur van een afspraak (#e5e7eb) en viel weg; nu een smalle, duidelijk donkerder lijn.
const PERSOON_VOEG = '#9ca3af';
const GEEN_AGENDA_PER_DAG = {};

/** Esc is al van iets anders: een open TaakPaneel, dialoog/keuzelijst/menu, of een invoerveld. */
export function escHoortBijIetsAnders(doel) {
  if (typeof document !== 'undefined' && document.querySelector('[data-taakpaneel="open"]')) return true;
  const el = doel instanceof Element ? doel : null;
  if (!el) return false;
  if (el.closest('input, textarea, select, [contenteditable="true"]')) return true;
  return Boolean(el.closest('[role="dialog"],[role="listbox"],[role="menu"],[role="presentation"]'));
}

/**
 * Eén persoon als rij blokken. Opties voor de uitklap op Capaciteit (weggelaten = Rooster):
 * `naamKolom` (breedte van de eerste kolom, css), `naamInhoud` (eigen inhoud van die kolom
 * in plaats van naam/rol/vrij), `zonderDagSignaal` (de hoofdrij erboven toont de dag al)
 * en `onRand` (randhandvatten, zie ProjectBlok). Dagstart: `ruim` (meer lucht tussen
 * personen), `toonRuimte` (dagtotaal ook als "+3u") en `betekenis` (kleur = soort werk,
 * mijlpalen met versie; zie utils/roosterBetekenis).
 *
 * Gememoiseerd: tijdens slepen (overCell), selecteren of een paneel openen tekent alleen de
 * rij die er echt toe doet opnieuw — geef dus stabiele props mee (zie RoosterGrid).
 */
export const PersoonRij = React.memo(function PersoonRij({
  person, label, kolommen, sjabloon, blokken, load, totaal, rules,
  overCell, selected, gedimd, onSelect, agendaPerDag = GEEN_AGENDA_PER_DAG, agendaOnbekend = null, weekendWerk = null, onTaakOpen, naamHint,
  naamKolom = `${NAAM_BREEDTE}px`, naamInhoud = null, zonderDagSignaal = false, onRand, ruim = false, toonRuimte = false,
  betekenis = false,
}) {
  const pad = ruim ? RIJ_PAD.ruim : RIJ_PAD.compact;
  // Uitgeklapte projectblokken: onthouden op taak-id, zodat het na verplaatsen van één taak
  // uitgeklapt blijft (het blok-id verandert dan).
  const [openTaken, setOpenTaken] = useState(() => new Set());
  const zichtbaar = useMemo(() => klapBlokkenUit(blokken, openTaken, kolommen), [blokken, openTaken, kolommen]);
  const reviews = useMemo(
    () => (betekenis ? reviewSegmenten(zichtbaar.geplaatst, kolommen) : []),
    [betekenis, zichtbaar, kolommen],
  );
  const rijRef = useRef(null);
  // Na inklappen met het toetsenbord de focus terug op het samengevoegde blok (anders verdwijnt
  // het gefocuste losse blok en ben je je plek kwijt).
  const herfocus = useRef(null);
  const onthoudFocus = (taakId) => {
    const actief = typeof document !== 'undefined' ? document.activeElement : null;
    herfocus.current = actief && actief !== document.body && rijRef.current?.contains(actief) ? taakId : null;
  };
  const wisselUitklap = (blok) => {
    onthoudFocus(blok.tasks[0]?.id);
    setOpenTaken((prev) => {
      const volgende = new Set(prev);
      if (blok.uitgeklaptUit) blok.uitgeklaptUit.forEach((id) => volgende.delete(id));
      else blok.tasks.forEach((t) => volgende.add(t.id));
      return volgende;
    });
  };
  // Esc klapt de losse taken van deze rij weer in — niet als Esc al iets anders doet (een open
  // TaakPaneel, keuzelijst of dialoog, typen in een veld, of een sleep/randversleep die Esc
  // afbreekt: zolang een muisknop/vinger ingedrukt is, is Esc van dat gebaar).
  const ietsOpen = openTaken.size > 0;
  useEffect(() => {
    if (!ietsOpen) return undefined;
    let ingedrukt = false;
    const neer = () => { ingedrukt = true; };
    const op = () => { ingedrukt = false; };
    const bijToets = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || ingedrukt) return;
      if (escHoortBijIetsAnders(e.target)) return;
      const actief = document.activeElement;
      const opBlok = actief && rijRef.current?.contains(actief) ? actief.getAttribute('data-taken') : null;
      herfocus.current = opBlok ? opBlok.split(' ')[0] : null;
      setOpenTaken(new Set());
    };
    document.addEventListener('pointerdown', neer, true);
    document.addEventListener('pointerup', op, true);
    document.addEventListener('pointercancel', op, true);
    window.addEventListener('blur', op);
    document.addEventListener('keydown', bijToets);
    return () => {
      document.removeEventListener('pointerdown', neer, true);
      document.removeEventListener('pointerup', op, true);
      document.removeEventListener('pointercancel', op, true);
      window.removeEventListener('blur', op);
      document.removeEventListener('keydown', bijToets);
    };
  }, [ietsOpen]);
  useEffect(() => {
    const id = herfocus.current;
    if (!id) return;
    herfocus.current = null;
    const veilig = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    rijRef.current?.querySelector(`[data-taken~="${veilig}"]`)?.focus();
  }, [zichtbaar]);
  const heeftBlokken = zichtbaar.geplaatst.length > 0;
  const lanes = Math.max(1, zichtbaar.laneCount);
  const sleeptHierheen = overCell?.personId === person.id;
  const rol = rolTekst(person);
  const overboekt = useMemo(
    () => kolommen.map((k) => !k.weekend && (load.uren[k.iso] || 0) > rules.hoursPerDay + 0.05),
    [kolommen, load, rules.hoursPerDay]
  );

  // Per dag: wat vult de dag naast de projectblokken (afspraken, klein werk)? Dat wordt
  // alleen een grijze vulling onderin de cel; details staan in de hover-tekst.
  const dagen = useMemo(() => {
    const projectLabels = new Set(blokken.geplaatst.flatMap((b) => b.tasks.map((t) => {
      const nr = String(t.projectNumber || '').trim();
      return `${nr ? `${nr} · ` : ''}${t.name || t.type || 'Taak'}`;
    })));
    return kolommen.map((k) => {
      if (k.weekend) return null;
      const staat = load.staat[k.iso];
      const dag = staat ? null : dagOverig(load.opbouw?.[k.iso], projectLabels);
      const uren = load.uren[k.iso] || 0;
      return {
        uren,
        aandeel: dag ? dag.totaal / (rules.hoursPerDay || 8) : 0,
        titel: dagTitel({ k, staat, uren, hoursPerDay: rules.hoursPerDay, dag, afspraken: agendaPerDag[k.iso], extra: load.extra?.[k.iso] }),
      };
    });
  }, [blokken, kolommen, load, rules.hoursPerDay, agendaPerDag]);
  // Kolommen onder een draaidagblok: het blok zegt het al, geen tweede "Draaidag" eronder.
  const onderDraaidag = useMemo(() => {
    const uit = new Set();
    zichtbaar.geplaatst.forEach((b) => { if (b.task?.bronDraaidag) for (let i = b.c0; i <= b.c1; i += 1) uit.add(i); });
    return uit;
  }, [zichtbaar]);
  const rijen = heeftBlokken ? Array(lanes).fill(`${LANE_H}px`) : [`${LEEG_H}px`];
  const teveel = totaal?.teveel || 0;
  // Geschatte hoogte voor content-visibility: rijen buiten beeld slaat de browser over bij
  // layout en paint (bladeren tekende anders alle 36 rijen opnieuw uit, ook onzichtbare).
  const hoogte = pad.boven + pad.onder + RAND_DIKTE[ruim ? 'ruim' : 'compact'] + (heeftBlokken ? lanes * LANE_H : LEEG_H) + VOET_H + rijen.length * LANE_GAP;

  return (
    <Box
      ref={rijRef}
      data-testid="persoon-rij"
      sx={{
        display: 'grid',
        gridTemplateColumns: `${naamKolom} ${sjabloon}`,
        gridTemplateRows: `${rijen.join(' ')} ${VOET_H}px`,
        rowGap: `${LANE_GAP}px`,
        pt: `${pad.boven}px`,
        pb: `${pad.onder}px`,
        borderBottom: ruim ? `${RAND_DIKTE.ruim}px solid ${PERSOON_VOEG}` : `1px solid ${RAND}`,
        contentVisibility: 'auto',
        containIntrinsicSize: `auto ${hoogte}px`,
        opacity: gedimd ? 0.45 : 1,
        transition: 'opacity .15s',
        '&:hover': { opacity: 1 },
        '&:hover .naamcel': { bgcolor: selected ? '#eef2ff' : '#fafafa' },
      }}
    >
      {/* Naamcel (of de eigen inhoud van de uitklap op Capaciteit) */}
      {naamInhoud ? (
        <Box sx={{ gridColumn: '1', gridRow: '1 / -1', mt: `-${pad.boven}px`, mb: `-${pad.onder}px`, minWidth: 0, display: 'flex', alignItems: 'flex-start' }}>
          {naamInhoud}
        </Box>
      ) : (
      <Box className="naamcel" onClick={onSelect} role="button" tabIndex={0} aria-pressed={!!selected}
        data-testid="rooster-naam"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(); } }}
        title={naamHint || (selected ? 'Klik om de selectie op te heffen' : 'Klik om te selecteren — daarna een taak in de bak aanklikken om toe te wijzen')}
        sx={{
        cursor: 'pointer',
        gridColumn: '1', gridRow: '1 / -1', mt: `-${pad.boven}px`, mb: `-${pad.onder}px`,
        display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.25,
        borderRight: `1px solid ${RAND}`, minWidth: 0,
        bgcolor: sleeptHierheen ? '#dbeafe' : selected ? '#eef2ff' : '#fff',
        boxShadow: sleeptHierheen ? 'inset 3px 0 0 #2563eb' : selected ? 'inset 3px 0 0 #6366f1' : 'none',
        transition: 'background-color 80ms',
      }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0, bgcolor: NEUTRAAL, color: GEDEMPT,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700,
        }}>
          {initialen(person.name || label)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap title={person.name} sx={{ fontSize: 13.5, fontWeight: sleeptHierheen ? 800 : 600, color: TEKST, lineHeight: 1.3 }}>
            {sleeptHierheen ? '→ ' : ''}{person.name || label}
          </Typography>
          {rol && (
            <Typography noWrap title={rol} sx={{ fontSize: 11, color: GEDEMPT, lineHeight: 1.25 }}>
              {rol}
            </Typography>
          )}
          {agendaOnbekend && (
            <Typography noWrap data-testid="agenda-onbekend"
              title="Outlook-agenda kon niet gelezen worden — meetings en afwezigheid tellen voor deze persoon niet mee, dus de bezetting kan te laag zijn."
              sx={{ fontSize: 11, color: GEDEMPT, fontStyle: 'italic', lineHeight: 1.25 }}>
              agenda onbekend
            </Typography>
          )}
        </Box>
        <LuieTooltip
          arrow
          title={`${fmtUren(totaal.gepland)}u gepland van ${fmtUren(totaal.capaciteit)}u beschikbaar (${totaal.pct}%)`
            + `${teveel > 0.05 ? ` · ${fmtUren(teveel)}u te veel op ${totaal.overboekt} dag${totaal.overboekt > 1 ? 'en' : ''}` : ''}`
            + `${teveel > 0.05 && totaal.ruimte > 0.05 ? ` · elders nog ${fmtUren(totaal.ruimte)}u vrij` : ''}`
            + `${totaal.vrijeDagen ? ` · ${totaal.vrijeDagen} dag${totaal.vrijeDagen > 1 ? 'en' : ''} afwezig` : ''}`}
        >
          {teveel >= 0.25 ? (
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, whiteSpace: 'nowrap', color: ROOD, flexShrink: 0 }}>
              {fmtUren(teveel)}u te veel
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.25, whiteSpace: 'nowrap', color: GEDEMPT, flexShrink: 0 }}>
              {totaal.ruimte > 0.05 ? `${fmtUren(totaal.ruimte)}u vrij` : 'vol'}
            </Typography>
          )}
        </LuieTooltip>
      </Box>
      )}

      {kolommen.map((k, idx) => (
        <DagCel
          key={k.iso}
          personId={person.id}
          k={k}
          idx={idx}
          staat={load.staat[k.iso]}
          aandeel={dagen[idx]?.aandeel || 0}
          titel={dagen[idx]?.titel}
          isOver={overCell?.personId === person.id && overCell?.date === k.iso}
          pad={pad}
          zonderVulling={betekenis}
        />
      ))}
      {zichtbaar.geplaatst.map((blok) => {
        if (blok.vast) return <VastBlok key={blok.id} blok={blok} onOpen={betekenis ? onTaakOpen : undefined} />;
        let rood = false;
        for (let i = blok.c0; i <= blok.c1; i += 1) if (overboekt[i]) rood = true;
        // Dagstart (3 nov): gepland over een afwezige dag → dat stuk van de balk gearceerd.
        const afwezig = betekenis ? afwezigInBlok(blok, kolommen, load.staat) : null;
        return <ProjectBlok key={blok.id} blok={blok} bronPersoonId={person.id} rood={rood} onKlik={wisselUitklap} onOpen={onTaakOpen} kolommen={kolommen} sjabloon={sjabloon} onRand={onRand} betekenis={betekenis} afwezig={afwezig} />;
      })}
      {/* Dagstart: "klant reviewt" tussen twee versies van hetzelfde werk (3 nov). */}
      {reviews.map((seg) => <ReviewArcering key={seg.sleutel} seg={seg} />)}
      {!zonderDagSignaal && kolommen.map((k, idx) => (dagen[idx] && !(onderDraaidag.has(idx) && String(load.staat[k.iso] || '').startsWith('draaidag')) ? (
        <DagSignaal
          key={`v-${k.iso}`}
          k={k}
          idx={idx}
          uren={dagen[idx].uren}
          staat={load.staat[k.iso]}
          hoursPerDay={rules.hoursPerDay}
          titel={dagen[idx].titel}
          toonRuimte={toonRuimte}
        />
      ) : null))}
      {weekendWerk && kolommen.map((k, idx) => (k.weekend && weekendWerk[k.iso]?.length
        ? <WeekendMarkering key={`w-${k.iso}`} k={k} idx={idx} items={weekendWerk[k.iso]} />
        : null))}
    </Box>
  );
});


// ─── Groepskop ───────────────────────────────────────────────────────────────

export function GroepKop({ label, aantal, open, onToggle, ruimte }) {
  return (
    <Box onClick={onToggle} sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.35,
      bgcolor: '#fff', borderBottom: `1px solid ${RAND}`, cursor: 'pointer',
      position: 'sticky', top: 0, zIndex: 4,
      '&:hover': { bgcolor: '#fafafa' },
    }}>
      {open ? <ExpandLessIcon sx={{ fontSize: 16, color: GEDEMPT }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: GEDEMPT }} />}
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEKST }}>{label}</Typography>
      <Typography sx={{ fontSize: 10.5, color: GEDEMPT }}>{aantal}</Typography>
      {ruimte !== undefined && (
        <Typography sx={{ fontSize: 10.5, color: GEDEMPT }}>· samen {fmtUren(ruimte)}u vrij</Typography>
      )}
    </Box>
  );
}

// ─── Bak: één rij per project ────────────────────────────────────────────────

export function BakRij({ project, kolommen, sjabloon, plaatsing, onTaakKlik, klikHint }) {
  const lanes = Math.max(1, plaatsing.laneCount);
  const kleur = projectKleur(project.tasks?.[0] || { projectId: project.projectId });
  const uren = useMemo(
    () => (project.tasks || []).reduce((s, t) => s + effectiveTaskHours(t, DEFAULT_CAPACITY_RULES).val, 0),
    [project.tasks]
  );
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: `${NAAM_BREEDTE}px ${sjabloon}`,
      gridTemplateRows: `repeat(${lanes}, ${LANE_H}px)`,
      rowGap: `${LANE_GAP}px`,
      py: '4px',
      borderBottom: `1px solid ${RAND}`,
    }}>
      <Box sx={{
        gridColumn: '1', gridRow: '1 / -1', my: '-4px', px: 1, py: 0.5,
        display: 'flex', alignItems: 'center', gap: 0.75, borderRight: `1px solid ${RAND}`, minWidth: 0,
        boxShadow: `inset 3px 0 0 ${kleur}`,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={project.projectName || ''} arrow disableInteractive enterDelay={300} placement="top-start">
            <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: TEKST, lineHeight: 1.3 }}>
              {project.projectName}
            </Typography>
          </Tooltip>
          <Typography noWrap sx={{ fontSize: 10.5, color: GEDEMPT, lineHeight: 1.3 }}>
            {project.tasks.length} ta{project.tasks.length === 1 ? 'ak' : 'ken'} · {fmtUren(uren)}u
          </Typography>
        </Box>
      </Box>
      {kolommen.map((k, idx) => (
        <Box key={k.iso} sx={{
          gridColumn: `${idx + 2}`, gridRow: '1 / -1', my: '-4px',
          borderLeft: kolomRand(k), background: k.weekend ? ARCERING : 'transparent',
        }} />
      ))}
      {plaatsing.geplaatst.map((plek) => (
        <TaakBalk key={plek.task.id} plek={plek} onClick={onTaakKlik ? (e) => { e.stopPropagation(); onTaakKlik(plek.task); } : undefined} klikHint={klikHint} />
      ))}
    </Box>
  );
}

// ─── Deliverable-chip (sleep hele deliverable → speeltuin) ───────────────────

export function DeliverableChip({ dlv, selected, onClick, tasks }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `dlv-${dlv.id}`,
    data: { type: 'deliverable', deliverable: dlv, tasks },
  });
  return (
    <Box ref={setNodeRef} {...attributes} {...listeners} sx={{ display: 'inline-flex', opacity: isDragging ? 0.5 : 1, touchAction: 'none' }}>
      <Chip
        label={`${dlv.name} (${dlv.taskCount})`}
        size="small"
        onClick={onClick}
        variant={selected ? 'filled' : 'outlined'}
        color={selected ? 'secondary' : 'default'}
        title="Klik om te filteren · sleep op een persoon om de hele deliverable in te passen"
        sx={{ height: 22, fontSize: 10.5, maxWidth: 220, cursor: 'grab',
          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
      />
    </Box>
  );
}
